import useAutoScroll from '../../../hooks/use-auto-scroll'
import {GROUP_HEADER_HEIGHT} from '../../common/group/styled-group-header'
import {OMNIBAR_HEIGHT} from '../../omnibar/omnibar'
import {useIsDraggingToFill} from '../bulk-fill-values'
import {CELL_HEIGHT, TABLE_HEADER_HEIGHT} from '../constants'
import {useIsMouseHeldWithTableFocus} from '../table-cell-bulk-selection'

type UseTableBulkActionAutoscrollParams = {
  scrollRef: React.RefObject<HTMLDivElement | null>
  isGrouped?: boolean
  horizontalScrollbarSize?: number | null
}

const SNAP_TO_GRID = CELL_HEIGHT + 1 // +1 to account for border

const EASE = (v: number) => v * v * v // Cubic for smoother ramp

const GROUP_OFFSET_TOP = TABLE_HEADER_HEIGHT + GROUP_HEADER_HEIGHT
const UNGROUPED_OFFSET_TOP = TABLE_HEADER_HEIGHT

const GROUP_OFFSET_BOTTOM = 0
const UNGROUPED_OFFSET_BOTTOM = OMNIBAR_HEIGHT

/**
 * Hook to enable autoscrolling during bulk actions.
 * Activates when the user is either:
 * - Dragging to fill cells (drag-to-fill)
 * - Holding the mouse down to select cells (long-clicking selection)
 */
export function useTableBulkActionsAutoscroll({
  scrollRef,
  isGrouped,
  horizontalScrollbarSize,
}: UseTableBulkActionAutoscrollParams) {
  const isDraggingToFill = useIsDraggingToFill()
  const isMouseDown = useIsMouseHeldWithTableFocus()
  const offsetTop = isGrouped ? GROUP_OFFSET_TOP : UNGROUPED_OFFSET_TOP
  const offsetBottom = (isGrouped ? GROUP_OFFSET_BOTTOM : UNGROUPED_OFFSET_BOTTOM) + (horizontalScrollbarSize ?? 0)
  const autoScrollEnabled = isDraggingToFill || isMouseDown

  useAutoScroll({
    active: autoScrollEnabled,
    scrollRef,
    strength: 8,
    offsetY: [offsetTop, offsetBottom],
    axis: 'y',
    snapToGrid: SNAP_TO_GRID,
    mouseMoveThresholdY: 3,
    ease: EASE,
    deadZoneRatioY: 0.08,
    maxBufferY: 250,
  })
}
